import { Avatar, CircularProgress, IconButton } from '@mui/material';
import PersonOutline from '@mui/icons-material/PersonOutline';
import type { MouseEvent, ReactElement } from 'react';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { useGetContributorProfilePhotoUrl } from '@/state/queries/contributors/use-get-contributor-profile-photo-url';
import { useTranslations } from '@/i18n/use-translations';
import { getContributorProfileReviewIconColor } from './speaker-profile-review-status';

type ScheduleContributorHeaderAvatarProps = {
  contributor: ContributorDto;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
};

/**
 * Header thumbnail: event-stored profile photo (if any), else contributor-provided photo URI, else placeholder.
 */
export function ScheduleContributorHeaderAvatar({ contributor, onClick }: ScheduleContributorHeaderAvatarProps) {
  const t = useTranslations();
  const eventProfile = contributor.profiles?.[0];
  const eventAssetName = eventProfile?.assetName;
  const { data: eventPhotoUrl, isLoading, isError } = useGetContributorProfilePhotoUrl(contributor.id, eventAssetName);
  const providedUri = contributor.userContributor?.providedProfile?.photoUri;

  const reviewIconColor = onClick ? getContributorProfileReviewIconColor(contributor) : undefined;

  const wrap = (node: ReactElement) =>
    onClick ? (
      <IconButton
        type="button"
        color={reviewIconColor}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
        aria-label={t('event.detail.contributors.manageProfile')}
        sx={{ p: 0, borderRadius: 1, width: 40, height: 40, flexShrink: 0 }}
      >
        {node}
      </IconButton>
    ) : (
      node
    );

  if (eventAssetName && isLoading) {
    return wrap(
      <Avatar variant="square" sx={{ width: 40, height: 40, pointerEvents: 'none' }}>
        <CircularProgress size={20} />
      </Avatar>,
    );
  }

  const useEventPhoto = Boolean(eventAssetName && eventPhotoUrl && !isError);
  const src = useEventPhoto ? eventPhotoUrl : providedUri || undefined;

  return wrap(
    <Avatar
      src={src || undefined}
      variant="square"
      sx={{ width: 40, height: 40, flexShrink: 0, pointerEvents: 'none' }}
    >
      {!src ? <PersonOutline sx={{ fontSize: '1.25rem' }} /> : null}
    </Avatar>,
  );
}
